import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { ContentfulImage } from '../../components/Contentful';
import './PackageDetailPage.css';

const PackageDetailPage: React.FC = () => {
  const { packageId, offerId } = useParams<{ packageId?: string; offerId?: string }>();
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // Asset mappings - using existing images as placeholders until we get the actual Figma assets
  const figmaAssets = {
    // Main diptych image - desert landscape with vehicle
    mainImage: images.destinationsPage.properties.atacama || '/assets/packages/main-image.png',
    
    // Scattered carousel images
    collageImage1: images.propertyPages.restaurantFeature || '/assets/packages/collage-1.png',
    collageImage2: images.propertyPages.villas13 || '/assets/packages/collage-2.png', 
    collageImage3: images.propertyPages.masterVilla || '/assets/packages/collage-3.png',
    
    // Full screen background - outdoor lounge area
    fullscreenImage: images.destinationsPage.properties.patagonia || '/assets/packages/fullscreen.png',
    
    // Package card images for "You may also like" section
    packageImage1: images.destinationsPage.properties.atacama || '/assets/packages/package-1.png',
    packageImage2: images.destinationsPage.properties.iguazu || '/assets/packages/package-2.png',
    packageImage3: images.destinationsPage.properties.mendoza || '/assets/packages/package-3.png',
    
    // UI Icons
    arrowIcon: images.navigation.arrowExact || '/assets/icons/arrow.svg',
    accordionIcon: '/assets/icons/accordion.svg',
    polygonIcon: '/assets/icons/polygon.svg',
  };

  // Package data with approved copy from content maps
  const getPackageData = (id: string | undefined) => {
    switch (id) {
      case 'stay-longer':
        return {
          title: 'Stay Longer. The longer you stay, the more you save.',
          destinations: 'ATACAMA, IGUAZU & SANTA CATARINA',
          description: 'Connect deeper with one night on us. Stay a minimum of two nights and enjoy a complimentary third night, giving you more time to explore and unwind. * Valid for stays from May to September. * Minimum of 2 nights stay. * Savings based on double occupancy.',
          terms: 'Valid for stays from May to September. Minimum of 2 nights stay. Savings based on double occupancy.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'combine-three':
        return {
          title: 'Combine Atacama, Patagonia & Iguazú',
          destinations: 'SAVE FROM USD 2.600 PER COUPLE',
          description: 'Combine all 3 Awasi Lodges over a 10-night trip to get a discount starting at USD 1.400, plus 3 complimentary nights in connection cities (Buenos Aires, Santiago or Punta Arenas). Minimum nights: 10 *Savings based on double occupancy. *Not valid between December 20th, and January 3rd.',
          terms: 'Minimum nights: 10. Savings based on double occupancy. Not valid between December 20th, and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'combine-two':
        return {
          title: 'Combine Atacama & Patagonia',
          destinations: 'SAVE FROM USD 1.500 PER COUPLE',
          description: 'Combine Awasi Atacama and Awasi Patagonia over a 7-night trip and get a discount starting at USD 700, plus 2 complimentary nights in connection cities (Santiago or Punta Arenas). Minimum nights: 7 *Savings based on double occupancy. *Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          terms: 'Minimum nights: 7. Savings based on double occupancy. Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 're-energize':
        return {
          title: 'Atacama RE-ENERGIZE wellness retreat',
          destinations: 'From 5-10 October 2025, Awasi Atacama invites you to RE-ENERGIZE',
          description: 'This edition of RE-ENERGIZE welcomes Teca Toscano, a leading figure in the field of self-knowledge, who will guide you through transformative practices set against the stillness and vastness of Atacama. It is our curated wellness retreat in the heart of the desert, designed to reconnect body, mind and soul.',
          terms: 'Dates: From 5-10 October 2025. This is a curated wellness retreat with limited availability.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'combine-adventure':
        return {
          title: 'Combine two or more of our lodges for the perfect South American Adventure',
          destinations: 'patagonia, atacama, iguazu, mendoza & santa catarina',
          description: 'When combining our destinations, enjoy complimentary stays at our favourite hotels in connecting cities, plus benefit from the local know-how from our experts who will help create your tailor-made experience.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'combined':
        return {
          title: 'Combine two or more of our lodges for the perfect South American Adventure',
          destinations: 'patagonia, atacama, iguazu, mendoza & santa catarina',
          description: 'At Awasi time and space are yours to own. Our lodges are designed to feel like the home of an old friend. guiding you through stunning landscapes, native flavors and hidden gems. It\'s a fusion of friendship, admiration for the place, and personal hospitality that creates a genuine connection which each guest.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'three-lodges':
        return {
          title: 'Combine Atacama, Patagonia & Iguazú',
          destinations: 'SAVE FROM USD 2.600 PER COUPLE',
          description: 'Combine all 3 Awasi Lodges over a 10-night trip to get a discount starting at USD 1.400, plus 3 complimentary nights in connection cities (Buenos Aires, Santiago or Punta Arenas). Minimum nights: 10 *Savings based on double occupancy. *Not valid between December 20th, and January 3rd.',
          terms: 'Minimum nights: 10. Savings based on double occupancy. Not valid between December 20th, and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'atacama-patagonia':
      case 'patagonia-atacama':
        return {
          title: 'Combine Atacama & Patagonia',
          destinations: 'SAVE FROM USD 1.500 PER COUPLE',
          description: 'Combine Awasi Atacama and Awasi Patagonia over a 7-night trip and get a discount starting at USD 700, plus 2 complimentary nights in connection cities (Santiago or Punta Arenas). Minimum nights: 7 *Savings based on double occupancy. *Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          terms: 'Minimum nights: 7. Savings based on double occupancy. Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage2
            },
            {
              title: 'RE-ENERGIZE wellness immersion',
              tag: 'atacama',
              image: figmaAssets.packageImage3
            }
          ]
        };
      case 'wellness-immersion':
        return {
          title: 'Atacama RE-ENERGIZE wellness retreat',
          destinations: 'From 5-10 October 2025, Awasi Atacama invites you to RE-ENERGIZE',
          description: 'This edition of RE-ENERGIZE welcomes Teca Toscano, a leading figure in the field of self-knowledge, who will guide you through transformative practices set against the stillness and vastness of Atacama. It is our curated wellness retreat in the heart of the desert, designed to reconnect body, mind and soul.',
          terms: 'Dates: From 5-10 October 2025. This is a curated wellness retreat with limited availability.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage3
            }
          ]
        };
      default:
        return {
          title: 'Package Not Found',
          destinations: '',
          description: 'The requested package could not be found.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: images.galleryMain,
          collageImages: [],
          fullscreenImage: images.galleryMain,
          relatedPackages: []
        };
    }
  };

  const packageData = getPackageData(packageId || offerId);

  // Handle horizontal scroll for carousel
  useEffect(() => {
    const handleScroll = () => {
      if (carouselRef.current) {
        const scrollLeft = carouselRef.current.scrollLeft;
        const scrollWidth = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
        const progress = (scrollLeft / scrollWidth) * 100;
        setScrollProgress(progress);
        
        // Calculate current section (4 sections total)
        const sectionWidth = carouselRef.current.scrollWidth / 4;
        const section = Math.floor(scrollLeft / sectionWidth);
        setCurrentSection(section);
      }
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="package-detail-page">
      {/* Navigation - Atacama variant as shown in Figma */}
      <div className="package-detail-navigation">
        <SmartNavigation forceVariant="light" />
      </div>

      {/* Main Carousel Container */}
      <div className="package-detail-container">
        {/* Scroll Progress Indicator */}
        <div className="package-detail-scroll-progress">
          <div 
            className="package-detail-scroll-progress-bar" 
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        {/* Horizontal Scrolling Carousel */}
        <div className="package-detail-carousel" ref={carouselRef}>
          {/* Section 1: Diptych - Text + Image */}
          <section className="package-detail-section package-detail-diptych">
            <div className="package-detail-text-block">
              {/* Back Button */}
              <button className="package-detail-back" onClick={() => navigate('/packages')}>
                <img src={figmaAssets.polygonIcon} alt="" className="package-detail-back-icon" />
                <span>back</span>
              </button>

              <div className="package-detail-content">
                <div className="package-detail-header">
                  <h1 className="package-detail-title">{packageData.title}</h1>
                  <p className="package-detail-destinations">{packageData.destinations}</p>
                </div>

                <div className="package-detail-body">
                  <p className="package-detail-description">{packageData.description}</p>
                  
                  {/* Terms & Conditions Accordion */}
                  <div className="package-detail-accordion">
                    <button 
                      className="package-detail-accordion-header"
                      onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                    >
                      <span>Terms & Conditions</span>
                      <img 
                        src={figmaAssets.accordionIcon} 
                        alt="" 
                        className={`package-detail-accordion-icon ${isAccordionOpen ? 'open' : ''}`}
                      />
                    </button>
                    {isAccordionOpen && (
                      <div className="package-detail-accordion-content">
                        <p>{packageData.terms}</p>
                      </div>
                    )}
                  </div>

                  <button className="package-detail-book-button">
                    <span>book now</span>
                  </button>
                </div>
              </div>
            </div>

            <div 
              className="package-detail-image"
              style={{ backgroundImage: `url('${packageData.image || images.destinationsPage.properties.atacama}')` }}
            />
          </section>

          {/* Section 2: Scattered Carousel - 3 Images */}
          <section className="package-detail-section package-detail-scattered">
            <div className="package-detail-scattered-container">
              <div 
                className="package-detail-scattered-image-1"
                style={{ backgroundImage: `url('${packageData.collageImages[0] || images.propertyPages.restaurantFeature}')` }}
              />
              <div 
                className="package-detail-scattered-image-2"
                style={{ backgroundImage: `url('${packageData.collageImages[1] || images.propertyPages.villas13}')` }}
              />
              <div 
                className="package-detail-scattered-image-3"
                style={{ backgroundImage: `url('${packageData.collageImages[2] || images.propertyPages.masterVilla}')` }}
              />
            </div>
          </section>

          {/* Section 3: Full Screen Image with Overlay */}
          <section className="package-detail-section package-detail-fullscreen">
            <div 
              className="package-detail-fullscreen-image"
              style={{ backgroundImage: `url('${packageData.fullscreenImage || images.destinationsPage.properties.patagonia}')` }}
            >
              <div className="package-detail-overlay-label">
                <h2>You may also like</h2>
              </div>
            </div>
          </section>

          {/* Section 4: Triptych - 3 Package Cards */}
          <section className="package-detail-section package-detail-triptych">
            <div className="package-detail-cards-container">
              {packageData.relatedPackages.map((pkg, index) => (
                <div key={index} className="package-detail-card">
                  <div className="package-detail-card-image-container">
                    <div 
                      className="package-detail-card-image"
                      style={{ backgroundImage: `url('${pkg.image || images.destinationsPage.properties.atacama}')` }}
                    />
                    <div className="package-detail-card-tag">
                      <span>{pkg.tag}</span>
                    </div>
                  </div>
                  <div className="package-detail-card-content">
                    <h3 className="package-detail-card-title">{pkg.title}</h3>
                    <button className="package-detail-card-link">
                      <span>{index === 2 ? 'view more' : 'read more'}</span>
                      <div className="package-detail-link-underline" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Navigation Dots */}
        <div className="package-detail-dots">
          {[0, 1, 2, 3].map((index) => (
            <button
              key={index}
              className={`package-detail-dot ${currentSection === index ? 'active' : ''}`}
              onClick={() => {
                if (carouselRef.current) {
                  const sectionWidth = carouselRef.current.scrollWidth / 4;
                  carouselRef.current.scrollTo({
                    left: sectionWidth * index,
                    behavior: 'smooth'
                  });
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Instagram Section with exact Figma styling */}
      <div className="package-detail-instagram">
        <h2 className="package-detail-instagram-title">
          Follow us on Instagram @awasiexperience
        </h2>
        <InstagramSection 
          posts={[
            { src: images.instagram1, alt: "Instagram post 1" },
            { src: images.instagram2, alt: "Instagram post 2" },
            { src: images.instagram3, alt: "Instagram post 3" },
            { src: images.instagram4, alt: "Instagram post 4" }
          ]}
        />
      </div>

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default PackageDetailPage;